import { useEffect, useState, useCallback } from 'react';
import { Modal, notification } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { debounce } from '@med-fe/util';
import { getIsSuccess, getSaveSuccess, getSelectedHOO, getTemplateDetails } from '../../templates-selector';
import {
  checkTemplateName,
  createTemplateDetails,
  createTemplateDraftDetails,
  resetTemplateNameValid,
} from '../../templates-actions';

export function useMainInfoCreate(form) {
  // Handle create logic (similar to handleUpdate in edit)

  const dispatch = useDispatch();
  const history = useHistory();
  const details = useSelector(getTemplateDetails);
  const detailsData = {};
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [patternName, setPatternName] = useState('');
  const [templateNameDirty, setTemplateNameDirty] = useState(false);
  const templateNameValid = useSelector((state: any) => state.TEMPLATES_MANAGEMENT_REDUCER?.templateNameValid);
  const hOOSelected = useSelector(getSelectedHOO);
  const [selectedHOO, setSelectedHOO] = useState(hOOSelected ?? []);
  const [createError, setCreateError] = useState('');

  // HOO Start/End and duration logic (must be before pace calculation)
  const [liveHooStart, setLiveHooStart] = useState('');
  const [liveHooEnd, setLiveHooEnd] = useState('');

  // Calculate pace for create mode
  function calculatePace(slots, start, end) {
    if (!slots || slots.length === 0) return 0;
    const filteredSlots = slots.filter((s) => {
      if (!s.examName) return true;
      return s.examName.toLowerCase() !== 'lunch';
    });
    const startMins = parseTime12(start);
    const endMins = parseTime12(end);
    let hours = (endMins - startMins) / 60;
    if (isNaN(hours) || hours <= 0) hours = 1;
    const pace = filteredSlots.length / hours;
    return Number(pace.toFixed(1));
  }

  // Get exams from details (if available)
  const exams = details?.examsConfiguration || [];
  const pace = calculatePace(exams, liveHooStart, liveHooEnd);
  const isSuccess = useSelector(getIsSuccess);
  const isSaveSuccess = useSelector(getSaveSuccess);

  // Enable submit only if patternName is non-empty, dirty, and valid
  useEffect(() => {
    let disabled = false;
    if (!patternName || !templateNameDirty) {
      disabled = true;
    }
    if (templateNameValid === false) {
      disabled = true;
    }
    if (templateNameDirty && (templateNameValid === null || templateNameValid === undefined)) {
      disabled = true;
    }
    setIsSubmitDisabled(disabled);
  }, [patternName, templateNameValid, templateNameDirty]);

  useEffect(() => {
    setSelectedHOO(hOOSelected ?? []);
  }, [hOOSelected]);

  // Debounced Redux action for pattern name validation
  const validateTemplateName = useCallback(
    debounce((id, templateName) => {
      dispatch(checkTemplateName({ id, templateName }));
    }, 600),
    [dispatch]
  );

  useEffect(() => {
    dispatch(resetTemplateNameValid());
    setPatternName('');
    setTemplateNameDirty(false);
    form.setFieldsValue({ ...details, patternName: '' });
  }, []);

  useEffect(() => {
    if (patternName) {
      const templateName = patternName;
      validateTemplateName(0, templateName);
    }
  }, [patternName, validateTemplateName]);

  useEffect(() => {
    let newHooStart = '',
      newHooEnd = '';
    if (patternName) {
      const match = patternName.match(/^(\d+)-(\d+)/);
      if (match) {
        newHooStart = normalizeHour(match[1], false, '');
        newHooEnd = normalizeHour(match[2], true, match[1]);
        setLiveHooStart(newHooStart);
        setLiveHooEnd(newHooEnd);

        const startMins = parseTime12(newHooStart);
        const endMins = parseTime12(newHooEnd);
        let duration = '';
        let roundedDuration = '';
        if (startMins !== null && endMins !== null && endMins > startMins) {
          const hours = (endMins - startMins) / 60;
          const rounded = roundHalfHourAwayFromZero(hours);
          duration = hours % 1 === 0 ? String(hours) : hours.toFixed(2).replace(/\.00$/, '');
          roundedDuration = rounded % 1 === 0 ? String(rounded) : rounded.toFixed(2).replace(/\.00$/, '');
        }
        form.setFieldsValue({ startAt: newHooStart, templateDuration: duration, templateHOODuration: roundedDuration });
        return;
      }
    }
    setLiveHooStart('');
    setLiveHooEnd('');
    form.setFieldsValue({ startAt: '', templateDuration: '', templateHOODuration: '' });
  }, [patternName, form]);

  function roundHalfHourAwayFromZero(duration) {
    // Multiply by 2, round away from zero, then divide back by 2
    const rounded = Math.sign(duration) * Math.round(Math.abs(duration) * 2);
    return Math.round((rounded / 2) * 100) / 100; // two decimal places
  }

  const normalizeHour = (timeStr: string, isEnd: boolean, startRaw?: string) => {
    if (!timeStr) return '';

    let hour = 0;
    let minutes = 0;
    let period: 'AM' | 'PM' = 'AM';
    const raw = timeStr.trim();

    // Parse hour and minutes
    if (raw.includes(':')) {
      const [h, m] = raw.split(':');
      hour = parseInt(h, 10);
      minutes = parseInt(m, 10);
    } else {
      if (raw.length <= 2) {
        hour = parseInt(raw, 10);
        minutes = 0;
      } else {
        hour = parseInt(raw.slice(0, -2), 10);
        minutes = parseInt(raw.slice(-2), 10);
      }
    }

    if (!isEnd) {
      // Start time rules: 7–11 AM, else PM
      if (hour >= 7 && hour <= 11) {
        period = 'AM';
      } else {
        period = 'PM';
      }
    } else {
      // End time rules
      const startHour = startRaw ? parseInt(startRaw, 10) : 0;

      if (startHour >= 7 && startHour <= 11) {
        // Start in AM range
        // Use > instead of >= to handle same-hour cases (e.g., "8-8" means 8 AM to 8 PM)
        if (hour > startHour && hour <= 11) {
          period = 'AM'; // same AM block
        } else {
          period = 'PM'; // otherwise PM (including same hour)
        }
      } else {
        // Start is PM → End is always PM unless wrapping
        period = 'PM';
      }
    }

    // Convert to 12-hour format
    if (hour === 0) hour = 12;
    if (hour > 12) hour -= 12;

    return `${hour}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  function parseTime12(str) {
    if (!str) return null;
    const [time, period] = str.split(' ');
    if (!time || !period) return null;
    let [h, m] = time.split(':');
    h = parseInt(h, 10);
    m = parseInt(m, 10);
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h * 60 + m;
  }

  /**
   * handleCreate - handles both draft and create actions
   * @param {boolean} isDraft - if true, save as draft; else, create
   */
  const handleCreate = async (isDraft = false) => {
    setCreateError('');
    const { patternName } = form.getFieldsValue();
    const templateName = patternName;
    // Prepare examsConfiguration from details (if available)
    const exams = details?.examsConfiguration || [];
    if (!exams.length) {
      notification.warning({
        message: 'No Slot Selected',
        description: 'Please select at least one slot before creating the template.',
        placement: 'topRight',
        duration: 5,
      });
      return;
    }

    // Validate HOO Start time < all time slots' start time
    if (exams.length > 0 && liveHooStart) {
      const hooStartMins = parseTime12(liveHooStart);
      for (const slot of exams) {
        const slotStartMins = parseTime12(slot.from);
        if (hooStartMins !== null && slotStartMins !== null && hooStartMins > slotStartMins) {
          notification.warning({
            message: 'Slot Outside Hours of Operation',
            description: `Slot must be between ${liveHooStart} and ${liveHooEnd} as per template name (${
              patternName || ''
            }).`,
            placement: 'topRight',
            duration: 5,
          });
          return;
        }
      }
    }

    // Validate HOO End time > all time slots' end time
    if (exams.length > 0 && liveHooEnd) {
      const hooEndMins = parseTime12(liveHooEnd);
      for (const slot of exams) {
        const slotEndMins = parseTime12(slot.to);
        if (hooEndMins !== null && slotEndMins !== null && hooEndMins < slotEndMins) {
          notification.warning({
            message: 'Slot Outside Hours of Operation',
            description: `Slot must be between ${liveHooStart} and ${liveHooEnd} as per template name (${
              patternName || ''
            }).`,
            placement: 'topRight',
            duration: 5,
          });
          return;
        }
      }
    }

    const templateMaintUploadDtos = exams.map((exam) => ({
      templateName,
      schedulerTemplateID: null,
      temptID: null,
      rangeNo: 0,
      resourceId: null,
      serviceGroupId: 0,
      temptName: exam.examName,
      temptDisplayName: exam.examName,
      restrictService: null,
      recordedByComputer: null,
      recordedDate: null,
      rotationWeek: null,
      temptItemID: exam.examId,
      templateItmId2: exam.examId,
      temptStartTime: exam.from,
      temptEndTime: exam.to,
      temptDayOfWeek: null,
      temptDuration: exam.durationOfExam,
      temptInterval: null,
      temptSelected: null,
    }));
    const payload = { templateMaintUploadDtos };

    // If isDraft, call a different API or dispatch a different action
    if (isDraft) {
      Modal.confirm({
        title: 'Confirmation',
        content: 'Are you sure you want to save this as a Draft?',
        okText: 'Yes',
        cancelText: 'No',
        onOk: async () => {
          const result = await dispatch(createTemplateDraftDetails(payload));
          if (result?.type === 'CREATE_TEMPLATEDRAFT_DETAILS') {
            history.push('/templates-management');
          }
        },
      });
    } else {
      const result = await dispatch(createTemplateDetails(payload));
      if (result?.type === 'CREATE_TEMPLATE_DETAILS') {
        history.push('/templates-management');
      }
    }
  };

  return {
    form,
    detailsData,
    isSubmitDisabled,
    patternName,
    setPatternName,
    templateNameDirty,
    setTemplateNameDirty,
    templateNameValid,
    liveHooStart,
    liveHooEnd,
    pace,
    handleCreate,
    createError,
  };
}
